import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { AppWindow, FileText, Globe, Plus } from 'lucide-react';
import {
  type Site,
  type SitePage,
  type UpsertPageRequest,
  createSite,
  listPages,
  listSites,
  publishSite,
  upsertPage,
} from '@/api/sites';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Select } from '@/components/ui/Select';
import { Textarea } from '@/components/ui/Textarea';

export function SitesPage() {
  const { t } = useTranslation();
  const [sites, setSites] = useState<Site[]>([]);
  const [selected, setSelected] = useState<Site | null>(null);
  const [pages, setPages] = useState<SitePage[]>([]);
  const [loading, setLoading] = useState(true);
  const [pagesLoading, setPagesLoading] = useState(false);
  const [showNewSite, setShowNewSite] = useState(false);
  const [showPage, setShowPage] = useState(false);

  const loadSites = useCallback(() => {
    setLoading(true);
    listSites()
      .then((rows) => {
        setSites(rows);
        setSelected((cur) => rows.find((s) => s.id === cur?.id) ?? rows[0] ?? null);
      })
      .catch(() => setSites([]))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    loadSites();
  }, [loadSites]);

  const loadPages = useCallback((id: string) => {
    setPagesLoading(true);
    listPages(id)
      .then(setPages)
      .catch(() => setPages([]))
      .finally(() => setPagesLoading(false));
  }, []);

  useEffect(() => {
    if (selected) loadPages(selected.id);
    else setPages([]);
  }, [selected, loadPages]);

  async function togglePublish() {
    if (!selected) return;
    await publishSite(selected.id, !selected.published);
    loadSites();
  }

  return (
    <div className="mx-auto max-w-5xl space-y-6 p-6">
      <header className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold text-[var(--color-lop-text-primary)]">{t('sites.title')}</h1>
          <p className="text-sm text-[var(--color-lop-text-secondary)]">{t('sites.subtitle')}</p>
        </div>
        <Button size="sm" onClick={() => setShowNewSite(true)} data-testid="sites-new">
          <Plus className="mr-1 h-4 w-4" />
          {t('sites.newSite')}
        </Button>
      </header>

      <div className="grid grid-cols-1 gap-6 md:grid-cols-[260px_1fr]">
        <aside className="space-y-2">
          <h2 className="px-1 text-xs font-medium uppercase tracking-wide text-[var(--color-lop-text-muted)]">
            {t('sites.sites')}
          </h2>
          {loading && <p className="px-1 text-sm text-muted-foreground">{t('common.loading')}</p>}
          {!loading && sites.length === 0 && <p className="px-1 text-sm text-muted-foreground">{t('sites.empty')}</p>}
          <ul className="space-y-1">
            {sites.map((s) => (
              <li key={s.id}>
                <button
                  type="button"
                  onClick={() => setSelected(s)}
                  data-testid="sites-site"
                  className={`flex w-full items-center gap-2 rounded-md px-3 py-2 text-left text-sm ${
                    selected?.id === s.id
                      ? 'bg-[var(--color-lop-accent)]/15 text-[var(--color-lop-text-primary)]'
                      : 'text-[var(--color-lop-text-secondary)] hover:bg-white/5'
                  }`}
                >
                  <AppWindow className="h-4 w-4 shrink-0 text-[var(--color-lop-text-muted)]" />
                  <span className="min-w-0 flex-1 truncate">{s.name}</span>
                  {s.published && <span className="h-2 w-2 shrink-0 rounded-full bg-emerald-400" title={t('sites.published')} />}
                </button>
              </li>
            ))}
          </ul>
        </aside>

        <section className="overflow-hidden rounded-lg border bg-card">
          {!selected ? (
            <p className="p-6 text-center text-sm text-muted-foreground">{t('sites.selectPrompt')}</p>
          ) : (
            <>
              <div className="flex items-center justify-between border-b px-4 py-3">
                <div className="min-w-0">
                  <p className="flex items-center gap-2 font-medium text-[var(--color-lop-text-primary)]">
                    {selected.name}
                    <span
                      className={`rounded-full px-2 py-0.5 text-[10px] font-medium ${
                        selected.published ? 'bg-emerald-900/30 text-emerald-300' : 'bg-zinc-800 text-zinc-400'
                      }`}
                    >
                      {selected.published ? t('sites.published') : t('sites.draft')}
                    </span>
                  </p>
                  <p className="flex items-center gap-1 text-xs text-[var(--color-lop-text-muted)]">
                    <Globe className="h-3 w-3" />
                    {selected.customDomain || `/${selected.slug}`} · {selected.siteType}
                  </p>
                </div>
                <div className="flex gap-2">
                  <Button size="sm" variant="outline" onClick={togglePublish} data-testid="sites-publish">
                    {selected.published ? t('sites.unpublish') : t('sites.publish')}
                  </Button>
                  <Button size="sm" onClick={() => setShowPage(true)} data-testid="sites-new-page">
                    <Plus className="mr-1 h-4 w-4" />
                    {t('sites.newPage')}
                  </Button>
                </div>
              </div>
              {pagesLoading && <p className="p-6 text-center text-sm text-muted-foreground">{t('common.loading')}</p>}
              {!pagesLoading && pages.length === 0 && (
                <p className="p-8 text-center text-sm text-muted-foreground">{t('sites.noPages')}</p>
              )}
              {!pagesLoading && pages.length > 0 && (
                <ul className="divide-y divide-[var(--color-lop-border)]">
                  {pages.map((p) => (
                    <li key={p.id} className="flex items-center gap-3 px-4 py-3" data-testid="sites-page-row">
                      <FileText className="h-4 w-4 shrink-0 text-[var(--color-lop-text-muted)]" />
                      <div className="min-w-0 flex-1">
                        <p className="truncate text-sm font-medium text-[var(--color-lop-text-primary)]">{p.title}</p>
                        <p className="truncate text-xs text-[var(--color-lop-text-muted)]">/{p.slug}</p>
                      </div>
                      <span
                        className={`rounded-full px-2 py-0.5 text-[10px] font-medium ${
                          p.published ? 'bg-emerald-900/30 text-emerald-300' : 'bg-zinc-800 text-zinc-400'
                        }`}
                      >
                        {p.published ? t('sites.published') : t('sites.draft')}
                      </span>
                    </li>
                  ))}
                </ul>
              )}
            </>
          )}
        </section>
      </div>

      {showNewSite && (
        <NewSiteModal
          onClose={() => setShowNewSite(false)}
          onCreated={() => {
            setShowNewSite(false);
            loadSites();
          }}
        />
      )}
      {showPage && selected && (
        <PageModal
          siteId={selected.id}
          onClose={() => setShowPage(false)}
          onSaved={() => {
            setShowPage(false);
            loadPages(selected.id);
          }}
        />
      )}
    </div>
  );
}

function NewSiteModal({ onClose, onCreated }: { onClose: () => void; onCreated: () => void }) {
  const { t } = useTranslation();
  const [name, setName] = useState('');
  const [slug, setSlug] = useState('');
  const [siteType, setSiteType] = useState('web');
  const [customDomain, setCustomDomain] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function submit() {
    if (!name.trim() || !slug.trim()) return;
    setSubmitting(true);
    setError(null);
    try {
      await createSite({
        name: name.trim(),
        slug: slug.trim(),
        siteType,
        customDomain: customDomain.trim() || undefined,
      });
      onCreated();
    } catch (err) {
      setError((err as Error)?.message ?? 'Failed to create site');
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <div className="w-full max-w-md rounded-lg border bg-card p-6 shadow-xl">
        <h3 className="mb-4 text-lg font-semibold">{t('sites.newModal.title')}</h3>
        {error && <p className="mb-3 text-sm text-red-400">{error}</p>}
        <label className="mb-1 block text-xs text-[var(--color-lop-text-secondary)]">{t('sites.newModal.name')}</label>
        <Input value={name} onChange={(e) => setName(e.target.value)} className="mb-3" data-testid="sites-name" />
        <div className="mb-4 grid grid-cols-2 gap-3">
          <div>
            <label className="mb-1 block text-xs text-[var(--color-lop-text-secondary)]">{t('sites.newModal.slug')}</label>
            <Input value={slug} onChange={(e) => setSlug(e.target.value)} data-testid="sites-slug" />
          </div>
          <div>
            <label className="mb-1 block text-xs text-[var(--color-lop-text-secondary)]">{t('sites.newModal.type')}</label>
            <Select value={siteType} onChange={(e) => setSiteType(e.target.value)} data-testid="sites-type">
              <option value="web">{t('sites.type.web')}</option>
              <option value="landing">{t('sites.type.landing')}</option>
              <option value="portal">{t('sites.type.portal')}</option>
            </Select>
          </div>
        </div>
        <label className="mb-1 block text-xs text-[var(--color-lop-text-secondary)]">
          {t('sites.newModal.customDomain')}
        </label>
        <Input value={customDomain} onChange={(e) => setCustomDomain(e.target.value)} className="mb-4" placeholder="example.com" />
        <div className="flex justify-end gap-2">
          <Button variant="outline" size="sm" onClick={onClose} disabled={submitting}>
            {t('common.cancel')}
          </Button>
          <Button size="sm" onClick={submit} disabled={submitting} data-testid="sites-submit">
            {t('common.create')}
          </Button>
        </div>
      </div>
    </div>
  );
}

function PageModal({ siteId, onClose, onSaved }: { siteId: string; onClose: () => void; onSaved: () => void }) {
  const { t } = useTranslation();
  const [slug, setSlug] = useState('');
  const [title, setTitle] = useState('');
  const [blocksText, setBlocksText] = useState('[]');
  const [seoTitle, setSeoTitle] = useState('');
  const [seoDescription, setSeoDescription] = useState('');
  const [published, setPublished] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function submit() {
    if (!slug.trim() || !title.trim()) return;
    let blocks: unknown = [];
    if (blocksText.trim()) {
      try {
        blocks = JSON.parse(blocksText);
      } catch {
        setError(t('sites.pageModal.invalidJson'));
        return;
      }
    }
    setSubmitting(true);
    setError(null);
    try {
      const req: UpsertPageRequest = {
        slug: slug.trim(),
        title: title.trim(),
        blocks,
        seoTitle: seoTitle.trim() || undefined,
        seoDescription: seoDescription.trim() || undefined,
        published,
      };
      await upsertPage(siteId, req);
      onSaved();
    } catch (err) {
      setError((err as Error)?.message ?? 'Failed to save page');
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <div className="max-h-[92vh] w-full max-w-lg overflow-y-auto rounded-lg border bg-card p-6 shadow-xl">
        <h3 className="mb-4 text-lg font-semibold">{t('sites.pageModal.title')}</h3>
        {error && <p className="mb-3 text-sm text-red-400">{error}</p>}
        <div className="mb-3 grid grid-cols-2 gap-3">
          <div>
            <label className="mb-1 block text-xs text-[var(--color-lop-text-secondary)]">
              {t('sites.pageModal.slug')}
            </label>
            <Input value={slug} onChange={(e) => setSlug(e.target.value)} data-testid="sites-page-slug" />
          </div>
          <div>
            <label className="mb-1 block text-xs text-[var(--color-lop-text-secondary)]">
              {t('sites.pageModal.pageTitle')}
            </label>
            <Input value={title} onChange={(e) => setTitle(e.target.value)} data-testid="sites-page-title" />
          </div>
        </div>
        <label className="mb-1 block text-xs text-[var(--color-lop-text-secondary)]">
          {t('sites.pageModal.blocks')}
        </label>
        <Textarea
          value={blocksText}
          onChange={(e) => setBlocksText(e.target.value)}
          rows={5}
          className="mb-1 font-mono text-xs"
          data-testid="sites-page-blocks"
        />
        <p className="mb-3 text-[10px] text-[var(--color-lop-text-muted)]">{t('sites.pageModal.blocksHint')}</p>
        <label className="mb-1 block text-xs text-[var(--color-lop-text-secondary)]">{t('sites.pageModal.seoTitle')}</label>
        <Input value={seoTitle} onChange={(e) => setSeoTitle(e.target.value)} className="mb-3" />
        <label className="mb-1 block text-xs text-[var(--color-lop-text-secondary)]">
          {t('sites.pageModal.seoDescription')}
        </label>
        <Textarea value={seoDescription} onChange={(e) => setSeoDescription(e.target.value)} rows={2} className="mb-3" />
        <label className="mb-4 flex items-center gap-2 text-sm">
          <input type="checkbox" checked={published} onChange={(e) => setPublished(e.target.checked)} />
          {t('sites.pageModal.publishNow')}
        </label>
        <div className="flex justify-end gap-2">
          <Button variant="outline" size="sm" onClick={onClose} disabled={submitting}>
            {t('common.cancel')}
          </Button>
          <Button size="sm" onClick={submit} disabled={submitting} data-testid="sites-page-submit">
            {t('common.save')}
          </Button>
        </div>
      </div>
    </div>
  );
}
